import { GameSounds } from '@ezugi/bootstrap/src/store/types';
import { HowlOptions } from '@ezugi/howler';

import { isSoundEnabledSelector } from '../../selectors/media';
import { RootState } from '../../types';
import cache, { createAndCacheSound } from './soundCache';
import soundsMap from './soundsMap';
import { Queue, queuedPlay } from './util';

const queue = new Queue();

const VITE_STATIC_URL = import.meta.env.VITE_STATIC_URL;
const SOUNDS_URL = `${VITE_STATIC_URL}/sounds`;

type SoundType = keyof typeof soundsMap;

type PlaySoundParams =
  | { type: SoundType; meta?: any }
  | { type?: any; meta: any };

const getSourcesFromSprite = async (
  identifier: string,
  assetsPrefix: GameSounds['assetsPrefix']
) => {
  const dirName = assetsPrefix ? `${SOUNDS_URL}/${assetsPrefix}` : SOUNDS_URL;

  const url = `${dirName}/${identifier}.json`;
  const resp = await fetch(url);

  const data = (await resp.json()) as unknown as HowlOptions;

  const files = Array.isArray(data.src) ? data.src : [ data.src ];

  const sources = files.map((name) => `${dirName}/${name}`);

  return { spriteJson: data, sources };
};

// TODO: https://ezugi1.atlassian.net/browse/ERD-15298
const playSound = async ({ type, meta }: PlaySoundParams, state: RootState) => {
  const {
    soundName,
    spriteName,
    onEnd = () => {},
    multiLanguages,
    languageCode = 'en',
    queueMode = false,
  } = meta || soundsMap[type];

  const { app } = state;

  const localizedSpriteName = languageCode === 'en'
    || (multiLanguages && !multiLanguages.includes(languageCode))
    ? spriteName
    : `${spriteName}_${languageCode}`;

  const identifier = multiLanguages
    ? localizedSpriteName
    : spriteName;

  let sound = cache[identifier || soundName];

  if (!soundName) return;

  // fetch sound and add to cache
  if (!sound) {
    try {
      const { spriteJson, sources } = await getSourcesFromSprite(
        identifier,
        app.sounds?.assetsPrefix
      );

      sound = await createAndCacheSound({
        soundName,
        spriteName: identifier,
        spriteJson,
        sources,
      });
    } catch (_err) {
      // Fallback to using the sprite from core
      // eslint-disable-next-line no-console
      console.warn(`Sprite ${identifier} not found. Failed to play sound`);
    }
  }

  // no sound found, return early
  // isSoundEnabledSelector checks if sound is enabled in the app to avoid playing sound when it's disabled
  if (!sound || !isSoundEnabledSelector(state)) {
    return;
  }

  // Get sound from cache or fetch & cache it
  sound.once('end', () => onEnd(playSound));

  if (queueMode) {
    queue.enqueue(() => queuedPlay(sound, soundName));
  } else {
    sound.play(soundName);
  }
};

export default playSound;
